import { renderRouteInPortal, RoutePortalContext } from "./routePortal";

/**
 * Produce el HTML autocontenido de una ruta de impresion renderizando los
 * componentes Print reales DENTRO de la app ya cargada (mismo store/auth/JS),
 * sin volver a arrancar la SPA ni hacer un segundo login.
 *
 * La mecanica del portal oculto (montaje, aislamiento de CSS, timeout, limpieza)
 * vive en `renderRouteInPortal`; aqui solo se define como se espera el "listo"
 * (la señal `DOC_READY` que ya emite `PrintPage`) y como se serializa el DOM.
 */

type RenderRouteOptions = {
    timeoutMs?: number;
    absolutizeAssets?: boolean; // convierte href/src relativos a absolutos
};

export function renderRouteToHtml(path: string, opts: RenderRouteOptions = {}): Promise<string> {
    const { timeoutMs = 20000, absolutizeAssets = true } = opts;

    return renderRouteInPortal<string>(
        path,
        (ctx) => waitForDocReady(ctx, absolutizeAssets),
        { timeoutMs }
    );
}

/**
 * Espera la señal `DOC_READY` de `PrintPage` (postMessage a esta misma ventana) y
 * serializa el contenedor mientras sigue montado.
 */
function waitForDocReady(ctx: RoutePortalContext, absolutizeAssets: boolean): Promise<string> {
    const { exportId, container, origin, registerCleanup } = ctx;

    return new Promise<string>((resolve, reject) => {
        const onMessage = (e: MessageEvent) => {
            if (e.origin !== window.location.origin) return;
            const data = e.data as { type?: string; exportId?: string } | null;
            if (data?.type !== "DOC_READY" || data?.exportId !== exportId) return;

            window.removeEventListener("message", onMessage);
            try {
                resolve(serializeContainer(container, origin, absolutizeAssets));
            } catch (err) {
                reject(err);
            }
        };

        registerCleanup(() => window.removeEventListener("message", onMessage));
        window.addEventListener("message", onMessage);
    });
}

/**
 * Construye un documento HTML autocontenido a partir del contenido renderizado
 * y de las hojas de estilo del documento actual (las mismas que aplican a la
 * vista de impresión real), de modo que Chromium lo renderice idéntico.
 */
function serializeContainer(container: HTMLElement, origin: string, absolutize: boolean): string {
    const headParts: string[] = [];
    document
        .querySelectorAll('link[rel="stylesheet"], style')
        .forEach((node) => headParts.push(node.outerHTML));

    const rawDoc =
        "<!DOCTYPE html><html><head><meta charset=\"utf-8\">" +
        headParts.join("\n") +
        "</head><body>" +
        container.innerHTML +
        "</body></html>";

    const parsed = new DOMParser().parseFromString(rawDoc, "text/html");

    // Las hojas de estilo de impresión quedan deshabilitadas en el documento vivo
    // para no afectar la app principal; en la salida del PDF deben ir activas.
    parsed.querySelectorAll("style[disabled], link[disabled]").forEach((el) => el.removeAttribute("disabled"));

    if (absolutize) absolutizeDocUrls(parsed, origin);
    return "<!DOCTYPE html>\n" + parsed.documentElement.outerHTML;
}

/**
 * Convierte href/src relativos en absolutos para que el render server-side
 * pueda resolver assets (imágenes, hojas de estilo) sin depender del origen.
 */
function absolutizeDocUrls(doc: Document, origin: string) {
    const absolutize = (attr: "href" | "src") => (el: Element) => {
        const raw = el.getAttribute(attr);
        if (!raw) return;
        if (/^(https?:)?\/\//i.test(raw) || raw.startsWith("data:") || raw.startsWith("blob:")) return;
        try {
            el.setAttribute(attr, new URL(raw, origin).toString());
        } catch {
            /* ignore */
        }
    };

    const absolutizeHref = absolutize("href");
    const absolutizeSrc = absolutize("src");

    doc.querySelectorAll("link[href]").forEach(absolutizeHref);
    doc.querySelectorAll("img[src]").forEach(absolutizeSrc);
    doc.querySelectorAll("script[src]").forEach(absolutizeSrc);
    doc.querySelectorAll("a[href]").forEach(absolutizeHref);
    doc.querySelectorAll("source[src]").forEach(absolutizeSrc);
}
